import React from "react";
import * as Mui from "@mui/material";
import * as XLSX from "xlsx";
import { CalendarMonth, Category, Payments, Person, Source, TaskAlt, DateRange, Groups } from "@mui/icons-material";
import { designColors, designRadii, designShell, designType, designWeights } from "services/constants/designTokens";
import { sharedTableBodyCellSx } from "components/DesignSystem/sharedUiPatterns";
import { addMonths, longMonth, monthsBetween, periodText, shortMonth } from "./bonusCalc";

// Shared bits for Payroll > Pay Inputs > Bonus.

export const TONES = {
  green: { bg: "#E7F8EF", fg: "#067647" },
  blue: { bg: "#EAF2FF", fg: "#1D4ED8" },
  amber: { bg: "#FFF4E5", fg: "#B54708" },
  red: { bg: "#FEF2F2", fg: "#B42318" },
  purple: { bg: "#F1EDFF", fg: "#5B4BD5" },
  slate: { bg: designColors.slate100, fg: designColors.slate600 },
};

// Inline chip (a <span>, so it can sit inside text such as a panel subtitle).
export const Chip = ({ label, tone = "slate", sx }) => {
  const colors = TONES[tone] || TONES.slate;
  return (
    <Mui.Box
      component="span"
      sx={{
        ...designType.captionText,
        fontWeight: designWeights.semibold,
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: designRadii.compactButton,
        backgroundColor: colors.bg,
        color: colors.fg,
        whiteSpace: "nowrap",
        lineHeight: 1.5,
        verticalAlign: "middle",
        ...sx,
      }}
    >
      {label}
    </Mui.Box>
  );
};

export const ENTRY_STATUS = {
  ready: { label: "Ready", tone: "green" },
  included: { label: "Included", tone: "purple" },
  processed: { label: "Processed", tone: "blue" },
  cancelled: { label: "Cancelled", tone: "slate" },
};

export const RUN_STATUS = {
  draft: { label: "Draft", tone: "slate" },
  added: { label: "Added to payroll", tone: "green" },
  processed: { label: "Processed", tone: "blue" },
  cancelled: { label: "Cancelled", tone: "slate" },
};

export const RESULT = {
  eligible: { label: "Eligible", tone: "green" },
  excluded: { label: "Excluded", tone: "red" },
  check: { label: "Check", tone: "amber" },
};

export const SOURCE = { run: "Bonus run", manual: "Manual", import: "Import" };

export const EntryStatusChips = ({ entry }) => (
  <Mui.Stack direction="row" spacing={0.75} component="span" sx={{ display: "inline-flex" }}>
    <Chip {...ENTRY_STATUS[entry.status]} />
    {entry.deferred ? <Chip label="Deferred" tone="amber" /> : null}
  </Mui.Stack>
);

export const SourceChip = ({ entry }) => {
  if (entry.source === "run") return <Chip label={`Bonus run ${entry.run_id}`} tone="blue" />;
  if (entry.source === "import") return <Chip label="Import" tone="amber" />;
  return <Chip label="Manual" tone="slate" />;
};

// ₹ amounts: whole rupees without decimals, others with 2.
export const money = (value) => {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) return "—";
  const number = Number(value);
  return number.toLocaleString("en-IN", Number.isInteger(number) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
export const rupees = (value) => (money(value) === "—" ? "—" : `₹${money(value)}`);
export const money2 = (value) =>
  value === null || value === undefined ? "—" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// "FY 2025–26" for a full financial year, else the months.
export const forPeriod = (from, to) => {
  if (from && to && from.endsWith("-04") && to.endsWith("-03") && Number(to.slice(0, 4)) === Number(from.slice(0, 4)) + 1) {
    return `FY ${from.slice(0, 4)}–${to.slice(2, 4)}`;
  }
  if (from && to && from.endsWith("-01") && to.endsWith("-12") && from.slice(0, 4) === to.slice(0, 4)) return `CY ${from.slice(0, 4)}`;
  const quarter = { "01-03": "Q4", "04-06": "Q1", "07-09": "Q2", "10-12": "Q3" }[`${from?.slice(5)}-${to?.slice(5)}`];
  if (quarter && from.slice(0, 4) === to.slice(0, 4)) return `${quarter} ${from.slice(0, 4)}`;
  if (from === to) return longMonth(from);
  // "Apr–Dec 2025" within one year, else "Oct 25 – Mar 26".
  if (from.slice(0, 4) === to.slice(0, 4)) return `${longMonth(from).slice(0, 3)}–${longMonth(to)}`;
  return `${shortMonth(from)} – ${shortMonth(to)}`;
};
export const forPeriodLong = (from, to) => {
  const short = forPeriod(from, to);
  const full = periodText(from, to);
  return short === full ? full : `${full} (${short})`;
};

// Month drop-down options (Apr 2023 … Dec 2027).
export const MONTH_OPTIONS = monthsBetween("2023-04", "2027-12").map((m) => ({ value: m, label: longMonth(m) }));
export const lastMonth = () => {
  const now = new Date();
  return addMonths(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`, -1);
};

export const DAYS_LABEL = { days_present: "Days Present", paid_days: "Paid Days" };

export const methodShort = (type) => {
  if (!type) return "—";
  if (type.method === "fixed_or_days") return type.calculation_basis === "days_wages" ? "Days' wages" : "Fixed amount";
  return { percent_of_wages: "% of wages", per_day: "Fixed amount per day", enter_amount: "Enter amount", formula: "Custom formula" }[type.method] || type.method;
};

export const wagesLabel = (type) => {
  const names = type?.wage_component_names || [];
  if (!names.length) return "—";
  return names.map((name) => (/dearness/i.test(name) ? "DA" : name)).join(" + ");
};

// Detail rows (label left, value right) for drawers and review cards.
export const DetailRows = ({ rows }) => (
  <Mui.Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.2fr)", columnGap: 2, rowGap: 1.25 }}>
    {rows.filter(Boolean).map((row) => (
      <React.Fragment key={row.label}>
        <Mui.Typography sx={{ ...designType.tableBodyCell, color: designColors.slate600, pl: row.indent ? 1.5 : 0 }}>
          {row.indent ? "· " : ""}{row.label}
        </Mui.Typography>
        <Mui.Typography component="div" sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, color: row.color || designColors.slate900, fontSize: row.big ? 17 : undefined }}>
          {row.value}
        </Mui.Typography>
      </React.Fragment>
    ))}
  </Mui.Box>
);

export const SectionCard = ({ title, action, children, tone, sx }) => (
  <Mui.Box
    sx={{
      ...designShell.sectionCard,
      p: 2,
      ...(tone === "green" ? { backgroundColor: "#F2FBF6", borderColor: "#B7E4CB" } : {}),
      ...(tone === "teal" ? { backgroundColor: designColors.tealTint, borderColor: "#B7E4DB" } : {}),
      ...sx,
    }}
  >
    {title || action ? (
      <Mui.Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Mui.Typography sx={designType.sectionHeading}>{title}</Mui.Typography>
        {action}
      </Mui.Stack>
    ) : null}
    {children}
  </Mui.Box>
);

const headCell = { ...designShell.tableHeadCellBase, py: 1, whiteSpace: "normal", lineHeight: 1.3 };
const bodyCell = { ...sharedTableBodyCellSx, ...designType.tableBodyCell, py: 1 };
const right = { textAlign: "right" };

// Month-by-month table for one employee (calculated row). Shows the days
// column named after the basis (spec B8).
export const MonthTable = ({ row, type }) => {
  const method = type?.method;
  const daysBasis = row.days_basis || type?.days_basis || "paid_days";
  const daysKey = daysBasis === "days_present" ? "days_present" : "paid_days";
  const showWages = method === "percent_of_wages" || (method === "fixed_or_days" && type?.calculation_basis === "days_wages");
  const months = row.months || [];
  const sumOf = (key) => months.reduce((sum, m) => sum + (Number(m[key]) || 0), 0);
  const bonusHead = method === "percent_of_wages" ? `Bonus ${Number(type.rate)}% (₹)` : "Bonus (₹)";
  return (
    <Mui.TableContainer sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card }}>
      <Mui.Table size="small">
        <Mui.TableHead>
          <Mui.TableRow>
            <Mui.TableCell sx={headCell}>Month</Mui.TableCell>
            <Mui.TableCell sx={{ ...headCell, ...right }}>{DAYS_LABEL[daysBasis]}</Mui.TableCell>
            {showWages ? <Mui.TableCell sx={{ ...headCell, ...right }}>{wagesLabel(type)} Earned (₹)</Mui.TableCell> : null}
            {method === "percent_of_wages" ? <Mui.TableCell sx={{ ...headCell, ...right }}>Wages Used (₹)</Mui.TableCell> : null}
            <Mui.TableCell sx={{ ...headCell, ...right }}>{bonusHead}</Mui.TableCell>
          </Mui.TableRow>
        </Mui.TableHead>
        <Mui.TableBody>
          {months.map((m) => (
            <Mui.TableRow key={m.month}>
              <Mui.TableCell sx={bodyCell}>{shortMonth(m.month)}</Mui.TableCell>
              <Mui.TableCell sx={{ ...bodyCell, ...right }}>{m.employed ? m[daysKey] : "—"}</Mui.TableCell>
              {showWages ? <Mui.TableCell sx={{ ...bodyCell, ...right }}>{m.earned_wage == null ? "—" : money(m.earned_wage)}</Mui.TableCell> : null}
              {method === "percent_of_wages" ? <Mui.TableCell sx={{ ...bodyCell, ...right }}>{m.wages_used == null ? "—" : money(m.wages_used)}</Mui.TableCell> : null}
              <Mui.TableCell sx={{ ...bodyCell, ...right }}>{m.bonus_unrounded == null ? "—" : money2(m.bonus_unrounded)}</Mui.TableCell>
            </Mui.TableRow>
          ))}
          <Mui.TableRow>
            <Mui.TableCell sx={{ ...bodyCell, fontWeight: designWeights.semibold }}>Total</Mui.TableCell>
            <Mui.TableCell sx={{ ...bodyCell, ...right, fontWeight: designWeights.semibold }}>{sumOf(daysKey)}</Mui.TableCell>
            {showWages ? <Mui.TableCell sx={{ ...bodyCell, ...right, fontWeight: designWeights.semibold }}>{money(sumOf("earned_wage"))}</Mui.TableCell> : null}
            {method === "percent_of_wages" ? <Mui.TableCell sx={{ ...bodyCell, ...right, fontWeight: designWeights.semibold }}>{money(sumOf("wages_used"))}</Mui.TableCell> : null}
            <Mui.TableCell sx={{ ...bodyCell, ...right, fontWeight: designWeights.semibold }}>{money2(row.due_unrounded)}</Mui.TableCell>
          </Mui.TableRow>
        </Mui.TableBody>
      </Mui.Table>
    </Mui.TableContainer>
  );
};

// One-line note under the month table (spec: included months listed).
export const monthNote = (row, type) => {
  const included = (row.employed_months || []);
  const parts = [];
  if (included.length) parts.push(`Months included: ${shortMonth(included[0])} – ${shortMonth(included[included.length - 1])} (${included.length}).`);
  if (type?.method === "percent_of_wages" && type.ceiling) parts.push(`Wages used = earned, capped at ₹${money(type.ceiling)}.`);
  if (row.days_counted_total != null && type?.min_days) {
    parts.push(`${DAYS_LABEL[row.days_basis] || "Days"} in period: ${row.days_counted_total} (min ${Number(type.min_days)} ✓).`);
  }
  if (row.to_month_note) parts.push(row.to_month_note);
  return parts.join(" ");
};

// Result box: Bonus due / Earlier bonus / Pay now always together (spec B6).
export const resultRows = (row, { payLabel = "Pay now", leftToPay = false } = {}) => [
  { label: `Bonus due (₹${money2(row.due_unrounded)}, rounded)`, value: rupees(row.due) },
  { label: "Earlier bonus", value: rupees(row.already_paid) },
  { label: "Paid in salary", value: rupees(row.paid_in_salary), indent: true },
  { label: "Waiting in payroll", value: rupees(row.waiting_in_payroll), indent: true },
  { label: payLabel, value: rupees(row.pay_now), color: "#067647" },
  leftToPay ? { label: "Left to pay", value: rupees(Math.max(0, (row.due || 0) - (row.already_paid || 0) - (row.pay_now || 0))) } : null,
];

// ---------- CustomFilter headers ----------

export const entryHeaders = [
  { name: "Date", key: "date_label", type: "string", icon: <CalendarMonth fontSize="small" /> },
  { name: "Employee", key: "employee", type: "autocomplete", icon: <Person fontSize="small" /> },
  { name: "Bonus Type", key: "bonus_type_name", type: "autocomplete", icon: <Category fontSize="small" /> },
  { name: "For Period", key: "for_period", type: "string", icon: <DateRange fontSize="small" /> },
  { name: "Amount (₹)", key: "amount", type: "number", icon: <Payments fontSize="small" />, showBetweenOperator: false },
  { name: "Source", key: "source_label", type: "autocomplete", icon: <Source fontSize="small" /> },
  { name: "Status", key: "status_label", type: "autocomplete", icon: <TaskAlt fontSize="small" /> },
  { name: "Payroll Cycle", key: "payroll_cycle_name", type: "autocomplete", icon: <DateRange fontSize="small" /> },
];

export const runHeaders = [
  { name: "Run", key: "id", type: "string", icon: <Source fontSize="small" /> },
  { name: "Bonus Type", key: "bonus_type_name", type: "autocomplete", icon: <Category fontSize="small" /> },
  { name: "For Period", key: "for_period", type: "string", icon: <DateRange fontSize="small" /> },
  { name: "Payment", key: "payment_label", type: "autocomplete", icon: <Payments fontSize="small" /> },
  { name: "Employees", key: "employees", type: "number", icon: <Groups fontSize="small" />, showBetweenOperator: false },
  { name: "Amount (₹)", key: "amount", type: "number", icon: <Payments fontSize="small" />, showBetweenOperator: false },
  { name: "Payroll Cycle", key: "payroll_cycle_name", type: "autocomplete", icon: <DateRange fontSize="small" /> },
  { name: "Status", key: "status_label", type: "autocomplete", icon: <TaskAlt fontSize="small" /> },
  { name: "Created By", key: "created_by", type: "autocomplete", icon: <Person fontSize="small" /> },
];

export const paymentLabel = (payMode, partPercent, balance = false) =>
  payMode === "part" ? `Part · ${Number(partPercent)}%` : balance ? "Balance" : "Full";

// ---------- .xlsx downloads ----------

const resultLabel = (row) => RESULT[row.result]?.label || row.result;

// Step 2 / run download: Summary, Month-wise and Excluded sheets (spec B8).
export const downloadRunWorkbook = ({ fileName, rows, type, from, to }) => {
  const months = monthsBetween(from, to);
  const daysLabel = DAYS_LABEL[type?.days_basis || "paid_days"];
  const summary = rows.map((row) => ({
    "Employee code": row.employee_code,
    Employee: row.employee_name,
    Site: row.site,
    Months: row.months_counted,
    [daysLabel]: row.days_counted_total ?? "",
    "Wages used (₹)": row.wages_used_total ?? "",
    "Bonus due (₹)": row.due ?? "",
    "Earlier bonus (₹)": row.already_paid ?? "",
    "Pay now (₹)": row.pay_now ?? "",
    Result: resultLabel(row),
    Reason: row.reason || "",
  }));
  const eligible = rows.filter((row) => row.result === "eligible");
  const monthwise = eligible.map((row) => {
    const line = { "Employee code": row.employee_code, Employee: row.employee_name };
    months.forEach((m) => {
      const cell = row.months.find((x) => x.month === m);
      line[`${shortMonth(m)} bonus`] = cell?.bonus_unrounded == null ? "" : Math.round(cell.bonus_unrounded * 100) / 100;
      line[`${shortMonth(m)} wages`] = cell?.wages_used ?? "";
    });
    line["Total (₹)"] = row.due;
    line["Earlier (₹)"] = row.already_paid;
    line["Pay now (₹)"] = row.pay_now;
    return line;
  });
  const totalLine = { "Employee code": "", Employee: `All ${eligible.length} eligible` };
  months.forEach((m) => {
    totalLine[`${shortMonth(m)} bonus`] = Math.round(eligible.reduce((sum, row) => sum + (row.months.find((x) => x.month === m)?.bonus_unrounded || 0), 0) * 100) / 100;
    totalLine[`${shortMonth(m)} wages`] = eligible.reduce((sum, row) => sum + (row.months.find((x) => x.month === m)?.wages_used || 0), 0);
  });
  totalLine["Total (₹)"] = eligible.reduce((sum, row) => sum + row.due, 0);
  totalLine["Earlier (₹)"] = eligible.reduce((sum, row) => sum + row.already_paid, 0);
  totalLine["Pay now (₹)"] = eligible.reduce((sum, row) => sum + row.pay_now, 0);
  const excluded = rows.filter((row) => row.result !== "eligible").map((row) => ({
    "Employee code": row.employee_code, Employee: row.employee_name, Site: row.site, Result: resultLabel(row), Reason: row.reason,
  }));
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(summary), "Summary");
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet([...monthwise, totalLine]), "Month-wise");
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(excluded.length ? excluded : [{ Employee: "None" }]), "Excluded");
  XLSX.writeFile(book, fileName);
};

export const downloadSheet = (fileName, sheetName, rows) => {
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(rows.length ? rows : [{}]), sheetName);
  XLSX.writeFile(book, fileName);
};

export const IMPORT_COLUMNS = ["Employee code", "Bonus type", "Period from", "Period to", "Amount", "Payroll cycle", "External reference", "Remarks"];

export const downloadImportTemplate = (types) => {
  const sample = types.find((type) => type.status !== "Inactive")?.name || "Performance Bonus";
  downloadSheet("bonus_import_template.xlsx", "Bonus", [{
    "Employee code": "E1021",
    "Bonus type": sample,
    "Period from": "2026-07",
    "Period to": "2026-09",
    Amount: 5000,
    "Payroll cycle": "",
    "External reference": "",
    Remarks: "Optional. Payroll cycle blank = default treatment; months as YYYY-MM or Apr 2026",
  }]);
};
